// src/scenes/AnalyticalDashboard/index.js
import React, { useEffect, useState, PureComponent } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Sector,
  Cell,
  AreaChart,
  Area,
  ComposedChart,
} from "recharts";
import { companyManagementUrl, customerservice } from "../../config";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import { tokens } from "../../theme";
import Joyride, { STATUS } from "react-joyride";
import { useTour } from "../../context/TourContext";

const base_url = companyManagementUrl.uri;

// Define tour steps for AnalyticalDashboard
const analyticalDashboardTourSteps = [
  {
    target: ".analytical-dashboard-container",
    content: "Welcome to the Analytical Dashboard! This dashboard provides insights into loan performance and customer demographics across branches and groups.",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: ".analytical-branch-filter",
    content: "Use the Branch filter to view product popularity data specific to a selected branch.",
    placement: "bottom",
  },
  {
    target: ".analytical-group-filters",
    content: "Select a Branch and Group to filter group-specific product popularity data.",
    placement: "bottom",
  },
  {
    target: ".analytical-loan-performance",
    content: "This chart shows loan performance per branch, including Cleared, Arrears, and Non-Performing percentages, along with total loans.",
    placement: "top",
  },
  {
    target: ".analytical-group-loan-performance",
    content: "View group loan performance per branch, highlighting repayment trends and total loans for specific groups.",
    placement: "top",
  },
  {
    target: ".analytical-marital-status",
    content: "The Marital Status Distribution chart displays the breakdown of customers by marital status, useful for demographic analysis.",
    placement: "top",
  },
  {
    target: ".analytical-business-types",
    content: "This chart shows the distribution of business types among customers, helping identify key sectors.",
    placement: "top",
  },
  {
    target: ".analytical-gender-branch",
    content: "The Gender Distribution Across Branches chart breaks down customers by gender for each branch.",
    placement: "top",
  },
  {
    target: ".analytical-gender-distribution",
    content: "This pie chart shows the overall gender distribution of customers.",
    placement: "top",
  },
  {
    target: ".analytical-age-distribution",
    content: "The Age Distribution chart displays the age ranges of customers, aiding in targeting specific demographics.",
    placement: "top",
  },
  {
    target: ".analytical-education-levels",
    content: "This chart shows the distribution of education levels among customers, useful for assessing customer profiles.",
    placement: "top",
  },
  {
    target: ".analytical-age-gender",
    content: "The Age Gender Distribution chart combines age ranges and gender to provide deeper demographic insights.",
    placement: "top",
  },
  {
    target: ".analytical-new-repeat-customers",
    content: "This chart compares new and repeat customers by gender, helping track customer retention.",
    placement: "top",
  },
];

const AnalyticalDashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [productPopularityData, setProductPopularityData] = useState([]);
  const [loanPerformanceData, setLoanPerformanceData] = useState([]);
  const [groupProductPopularityData, setGroupProductPopularityData] = useState([]);
  const [groupLoanPerformanceData, setGroupLoanPerformanceData] = useState([]);
  const [maritalStatusDistributionData, setMaritalStatusDistributionData] = useState([]);
  const [businessTypesBarchartData, setBusinessTypesBarchartData] = useState([]);
  const [genderDistributionByRranchData, setGenderDistributionByRranchData] = useState([]);
  const [ageDistributionData, setAgeDistributionData] = useState([]);
  const [genderDistributionData, setGenderDistributionData] = useState([]);
  const [educationLevelDistributionData, setEducationLevelDistributionData] = useState([]);
  const [ageGenderDistributionData, setAgeGenderDistributionData] = useState([]);
  const [newRepeatCustomersByGenderData, setNewRepeatCustomersByGenderData] = useState([]);
  const [branchData, setBranchData] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [groupData, setGroupData] = useState([]);
  const [groupId, setGroupId] = useState(null);
  const [groupBranchId, setGroupBranchId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Tour state
  const { isFirstTime, touredPages, markPageAsToured } = useTour();
  const [runTour, setRunTour] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const currentPath = window.location.pathname;

  useEffect(() => {
    if (isFirstTime && !touredPages.includes(currentPath) && !loading) {
      setShowModal(true);
    }
  }, [isFirstTime, touredPages, currentPath, loading]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      markPageAsToured(currentPath);
      setShowModal(false);
    }
  };

  const startTour = () => {
    setRunTour(true);
    setShowModal(false);
  };

  const cancelTour = () => {
    setShowModal(false);
    markPageAsToured(currentPath);
  };

  useEffect(() => {
    async function fetchAllData() {
      try {
        setLoading(true);
        // Fetch branches data
        const branchDataResponse = await fetchJsonDynamic("branch", null, null, base_url);
        if (branchDataResponse?.results) {
          const results = JSON.parse(branchDataResponse.results);
          setBranchData(results || []);
        }
        const groupDataResponse = await fetchJsonDynamic("allGroups", null, null, customerservice.uri);
        if (groupDataResponse?.results) {
          const results = JSON.parse(groupDataResponse.results);
          setGroupData(results || []);
        }

        // Fetch chart data
        const loanPerformanceDataResponse = await fetchJsonDynamic(
          "loanPerformance",
          "dashboardMetrics",
          { companyId: null },
          base_url
        );
        if (loanPerformanceDataResponse?.results) {
          const results = JSON.parse(loanPerformanceDataResponse.results);
          setLoanPerformanceData(results || []);
        }

        const groupLoanPerformanceDataResponse = await fetchJsonDynamic(
          "groupLoanPerformance",
          "dashboardMetrics",
          { companyId: null },
          base_url
        );
        if (groupLoanPerformanceDataResponse?.results) {
          const results = JSON.parse(groupLoanPerformanceDataResponse.results);
          setGroupLoanPerformanceData(results || []);
        }

        const maritalStatusDistributionDataResponse = await fetchJsonDynamic(
          "maritalStatusDistribution",
          "dashboardMetrics",
          { companyId: null },
          base_url
        );
        if (maritalStatusDistributionDataResponse?.results) {
          const results = JSON.parse(maritalStatusDistributionDataResponse.results);
          setMaritalStatusDistributionData(results || []);
        }

        const businessTypesBarchartDataResponse = await fetchJsonDynamic(
          "businessTypesBarchart",
          "dashboardMetrics",
          { companyId: null },
          base_url
        );
        if (businessTypesBarchartDataResponse?.results) {
          const results = JSON.parse(businessTypesBarchartDataResponse.results);
          setBusinessTypesBarchartData(results || []);
        }

        const genderDistributionByRranchDataResponse = await fetchJsonDynamic(
          "genderDistributionByRranch",
          "dashboardMetrics",
          { companyId: null },
          base_url
        );
        if (genderDistributionByRranchDataResponse?.results) {
          const rawData = JSON.parse(genderDistributionByRranchDataResponse.results);
          const transformedData = rawData.reduce((acc, curr) => {
            const branch = acc.find((b) => b.BranchName === curr.BranchName);
            if (branch) {
              branch[curr.GenderName] = curr.Value;
            } else {
              acc.push({
                BranchName: curr.BranchName,
                [curr.GenderName]: curr.Value,
              });
            }
            return acc;
          }, []);
          setGenderDistributionByRranchData(transformedData || []);
        }

        const ageDistributionDataResponse = await fetchJsonDynamic(
          "ageDistribution",
          "dashboardMetrics",
          { companyId: null },
          base_url
        );
        if (ageDistributionDataResponse?.results) {
          const results = JSON.parse(ageDistributionDataResponse.results);
          setAgeDistributionData(results || []);
        }

        const genderDistributionDataResponse = await fetchJsonDynamic(
          "genderDistribution",
          "dashboardMetrics",
          { companyId: null },
          base_url
        );
        if (genderDistributionDataResponse?.results) {
          const results = JSON.parse(genderDistributionDataResponse.results);
          setGenderDistributionData(results || []);
        }

        const educationLevelDistributionDataResponse = await fetchJsonDynamic(
          "educationLevelDistribution",
          "dashboardMetrics",
          { companyId: null },
          base_url
        );
        if (educationLevelDistributionDataResponse?.results) {
          const results = JSON.parse(educationLevelDistributionDataResponse.results);
          setEducationLevelDistributionData(results || []);
        }

        const ageGenderDistributionDataResponse = await fetchJsonDynamic(
          "ageGenderDistribution",
          "dashboardMetrics",
          { companyId: null },
          base_url
        );
        if (ageGenderDistributionDataResponse?.results) {
          const results = JSON.parse(ageGenderDistributionDataResponse.results);
          const transformedData = results.reduce((acc, curr) => {
            const age = acc.find((b) => b.AgeRange === curr.AgeRange);
            if (age) {
              age[curr.Gender] = curr.Value;
            } else {
              acc.push({
                AgeRange: curr.AgeRange,
                [curr.Gender]: curr.Value,
              });
            }
            return acc;
          }, []);
          setAgeGenderDistributionData(transformedData || []);
        }

        const newRepeatCustomersByGenderDataResponse = await fetchJsonDynamic(
          "newRepeatCustomersByGender",
          "dashboardMetrics",
          { companyId: null },
          base_url
        );
        if (newRepeatCustomersByGenderDataResponse?.results) {
          const results = JSON.parse(newRepeatCustomersByGenderDataResponse.results);
          const transformedData = results.reduce((acc, curr) => {
            const type = acc.find((b) => b.CustomerType === curr.CustomerType);
            if (type) {
              type[curr.Gender] = curr.Value;
            } else {
              acc.push({
                CustomerType: curr.CustomerType,
                [curr.Gender]: curr.Value,
              });
            }
            return acc;
          }, []);
          setNewRepeatCustomersByGenderData(transformedData || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchAllData();
  }, []);

  const fetchProductPopularity = async () => {
    try {
      const productPopularityDataResponse = await fetchJsonDynamic(
        "productPopularity",
        "dashboardMetrics",
        { companyId: null, branchId: branchId },
        base_url
      );
      if (productPopularityDataResponse?.results) {
        const results = JSON.parse(productPopularityDataResponse.results);
        setProductPopularityData(results || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  useEffect(() => {
    fetchProductPopularity();
  }, [branchId]);

  const fetchGroupProductPopularity = async () => {
    try {
      const groupProductPopularityDataResponse = await fetchJsonDynamic(
        "groupProductPopularity",
        "dashboardMetrics",
        { companyId: null, branchId: groupBranchId, groupId: groupId },
        base_url
      );
      if (groupProductPopularityDataResponse?.results) {
        const results = JSON.parse(groupProductPopularityDataResponse.results);
        setGroupProductPopularityData(results || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  useEffect(() => {
    fetchGroupProductPopularity();
  }, [groupBranchId, groupId]);

  const selectStyle = {
    height: "100%",
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      padding: "8px",
    },
  };

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const branchOptions = branchData.map((branch) => ({
    value: branch.Id,
    label: branch.Name,
  }));

  const groupOptions = groupData.map((group) => ({
    value: group.Id,
    label: group.Name,
  }));

  const handleBranchChange = (event) => {
    const selectedBranchId = event.target.value;
    setBranchId(selectedBranchId);
  };

  const handleBranchGroupChange = (event) => {
    const selectedGroupBranchId = event.target.value;
    setGroupBranchId(selectedGroupBranchId);
    setGroupId(null);
  };

  const handleGroupChange = (event) => {
    const selectedGroupId = event.target.value;
    setGroupId(selectedGroupId);
  };

  return (
    <Box sx={{ padding: theme.spacing(3) }} className="analytical-dashboard-container">
      {/* Joyride Tour */}
      <Joyride
        steps={analyticalDashboardTourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: colors.blueAccent[500],
            backgroundColor: colors.primary[400],
            textColor: colors.grey[100],
            zIndex: 1000,
          },
        }}
      />

      {/* First-Time Modal */}
      <Dialog open={showModal} onClose={cancelTour}>
        <DialogTitle>Welcome to the Analytical Dashboard!</DialogTitle>
        <DialogContent>
          This dashboard provides detailed insights into loan performance and customer demographics. Would you like a guided tour to explore its features?
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelTour}>Skip</Button>
          <Button onClick={startTour} variant="contained">
            Start Tour
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="h4" gutterBottom>
        Analytical Dashboard
      </Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} className="analytical-branch-filter">
            <Paper
              elevation={3}
              sx={{
                padding: theme.spacing(2),
                background: `${colors.primary[400]} !important`,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Product Popularity (Branch Selection)
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <FormControl sx={{ flex: 1, minWidth: 100 }}>
                  <InputLabel>Select Branch...</InputLabel>
                  <Select
                    label="Select Branch..."
                    value={branchId}
                    onChange={handleBranchChange}
                    sx={selectStyle}
                    className="analytical-branch-select"
                    aria-label="Branch Filter"
                  >
                    {branchOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={productPopularityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="ProductName" />
                  <YAxis
                    label={{
                      value: "Count",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar type="monotone" dataKey="UsageCount" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} className="analytical-loan-performance">
            <Paper
              elevation={3}
              sx={{
                padding: theme.spacing(2),
                background: `${colors.primary[400]} !important`,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Loan Performance per Branch
              </Typography>
              <ResponsiveContainer width="100%" height={270}>
                <ComposedChart data={loanPerformanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="BranchName" />
                  <YAxis
                    yAxisId="left"
                    label={{
                      value: "Percentage (%)",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    label={{
                      value: "Total Loans",
                      angle: -90,
                      position: "insideRight",
                    }}
                  />
                  <Tooltip
                    formatter={(value, name) =>
                      name === "Total Loans" ? value : `${value}%`
                    }
                  />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="ClearedPercentage"
                    stackId="a"
                    fill="#82ca9d"
                    name="Cleared (%)"
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="ArrearsPercentage"
                    stackId="a"
                    fill="#ffc658"
                    name="Arrears (%)"
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="NonPerformingPercentage"
                    stackId="a"
                    fill="#d84e47"
                    name="Non-Performing (%)"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="TotalLoans"
                    stroke="#4e73df"
                    name="Total Loans"
                    strokeWidth={2}
                    activeDot={{ r: 8 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} className="analytical-group-filters">
            <Paper
              elevation={3}
              sx={{
                padding: theme.spacing(2),
                background: `${colors.primary[400]} !important`,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Group Product Popularity (Filtered by Branch and Group)
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }} className="analytical-group-selects">
                <FormControl sx={{ flex: 1, minWidth: 100 }}>
                  <InputLabel>Select Branch...</InputLabel>
                  <Select
                    label="Select Branch..."
                    value={groupBranchId}
                    onChange={handleBranchGroupChange}
                    sx={selectStyle}
                    className="analytical-group-branch-select"
                    aria-label="Group Branch Filter"
                  >
                    {branchOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ flex: 1, minWidth: 100 }}>
                  <InputLabel>Select Group...</InputLabel>
                  <Select
                    label="Select Group..."
                    value={groupId}
                    onChange={handleGroupChange}
                    sx={selectStyle}
                    className="analytical-group-select"
                    aria-label="Group Filter"
                  >
                    {groupOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={groupProductPopularityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="ProductName" tickMargin={10} />
                  <YAxis
                    label={{
                      value: "Count",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <Tooltip />
                  <Legend />
                  <Bar type="monotone" dataKey="TotalCount" fill="#00C49F" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} className="analytical-group-loan-performance">
            <Paper
              elevation={3}
              sx={{
                padding: theme.spacing(2),
                background: `${colors.primary[400]} !important`,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Group Loan Performance per Branch
              </Typography>
              <ResponsiveContainer width="100%" height={270}>
                <ComposedChart data={groupLoanPerformanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="BranchName" />
                  <YAxis
                    yAxisId="left"
                    label={{
                      value: "Percentage (%)",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    label={{
                      value: "Total Loans",
                      angle: -90,
                      position: "insideRight",
                    }}
                  />
                  <Tooltip
                    formatter={(value, name) =>
                      name === "Total Loans" ? value : `${value}%`
                    }
                  />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="ClearedPercentage"
                    stackId="a"
                    fill="#82ca9d"
                    name="Cleared (%)"
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="ArrearsPercentage"
                    stackId="a"
                    fill="#ffc658"
                    name="Arrears (%)"
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="NonPerformingPercentage"
                    stackId="a"
                    fill="#d84e47"
                    name="Non-Performing (%)"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="TotalLoans"
                    stroke="#4e73df"
                    name="Total Loans"
                    strokeWidth={2}
                    activeDot={{ r: 8 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4} className="analytical-marital-status">
            <Paper
              elevation={3}
              sx={{
                padding: theme.spacing(2),
                background: `${colors.primary[400]} !important`,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Marital Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={maritalStatusDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.Name}: ${entry.Value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="Value"
                  >
                    {maritalStatusDistributionData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8} className="analytical-business-types">
            <Paper
              elevation={3}
              sx={{
                padding: theme.spacing(2),
                background: `${colors.primary[400]} !important`,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Business Types
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={businessTypesBarchartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="Name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8} className="analytical-gender-branch">
            <Paper
              elevation={3}
              sx={{
                padding: theme.spacing(2),
                background: `${colors.primary[400]} !important`,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Gender Distribution Across Branches
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={genderDistributionByRranchData}>
                  <XAxis dataKey="BranchName" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Female" stackId="a" fill="#8884d8" />
                  <Bar dataKey="Male" stackId="a" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4} className="analytical-gender-distribution">
            <Paper
              elevation={3}
              sx={{
                padding: theme.spacing(2),
                background: `${colors.primary[400]} !important`,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Gender Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={genderDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.Name}: ${entry.Value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="Value"
                  >
                    {genderDistributionData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8} className="analytical-age-distribution">
            <Paper
              elevation={3}
              sx={{
                padding: theme.spacing(2),
                background: `${colors.primary[400]} !important`,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Age Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={ageDistributionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="Name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="Value" stroke="#00c49f" />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4} className="analytical-education-levels">
            <Paper
              elevation={3}
              sx={{
                padding: theme.spacing(2),
                background: `${colors.primary[400]} !important`,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Education Levels
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={educationLevelDistributionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="Name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8} className="analytical-age-gender">
            <Paper
              elevation={3}
              sx={{
                padding: theme.spacing(2),
                background: `${colors.primary[400]} !important`,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Age Gender Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={ageGenderDistributionData}>
                  <XAxis dataKey="AgeRange" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Female" stackId="a" fill="#8884d8" />
                  <Bar dataKey="Male" stackId="a" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4} className="analytical-new-repeat-customers">
            <Paper
              elevation={3}
              sx={{
                padding: theme.spacing(2),
                background: `${colors.primary[400]} !important`,
              }}
            >
              <Typography variant="h6" gutterBottom>
                New or Repeat Customers by Gender
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={newRepeatCustomersByGenderData}>
                  <XAxis dataKey="CustomerType" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Female" stackId="a" fill="#8884d8" />
                  <Bar dataKey="Male" stackId="a" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AnalyticalDashboard;